// Room-camera helpers for /eye: find people (MediaPipe), digitally zoom on each head from the full-res frame,
// and remember the owner's body (clothes colours + build) for the day once his face is confirmed.
const MP = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14";
const PERSON = "https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite";
const DAY_KEY = "nx_body_day";
export const BODY_MATCH = 0.86;

export async function loadPersons() {
  const v = await new Function("u", "return import(u)")(`${MP}/vision_bundle.mjs`);
  const files = await v.FilesetResolver.forVisionTasks(`${MP}/wasm`);
  const mk = (delegate) => v.ObjectDetector.createFromOptions(files, {
    baseOptions: { modelAssetPath: PERSON, delegate }, runningMode: "VIDEO", categoryAllowlist: ["person"], scoreThreshold: 0.4, maxResults: 4 });
  return mk("GPU").catch(() => mk("CPU"));
}

export const findPeople = (det, v) => (det.detectForVideo(v, performance.now()).detections || []).map((d) => d.boundingBox);

const center = (f) => { const b = f.detection.box; return { x: b.x + b.width / 2, y: b.y + b.height / 2 }; };
export const inBox = (f, b) => { const c = center(f); return c.x >= b.originX && c.x <= b.originX + b.width && c.y >= b.originY && c.y <= b.originY + b.height; };

// Crop each person's head area from the full-quality frame, enlarge it, and run face recognition on the crop.
export async function zoomFaces(fa, v, boxes, fullInput) {
  const out = [];
  for (const b of boxes.slice(0, 3)) {
    const side = Math.max(b.width * 0.9, b.height * 0.32);
    const sx = Math.max(0, b.originX + b.width / 2 - side / 2), sy = Math.max(0, b.originY - side * 0.08);
    const sw = Math.min(side, v.videoWidth - sx), sh = Math.min(side, v.videoHeight - sy);
    if (sw < 20 || sh < 20 || sw * (fullInput / v.videoWidth) > 150) continue; // head already big enough for the normal pass
    const scale = Math.min(4, 416 / sw);
    const c = document.createElement("canvas");
    c.width = Math.round(sw * scale); c.height = Math.round(sh * scale);
    c.getContext("2d").drawImage(v, sx, sy, sw, sh, 0, 0, c.width, c.height);
    const r = await fa.detectAllFaces(c, new fa.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.4 })).withFaceLandmarks().withFaceDescriptors();
    const m = (p) => ({ x: sx + p.x / scale, y: sy + p.y / scale });
    r.forEach((f) => {
      const bb = f.detection.box, o = m(bb);
      out.push({ zoom: scale, region: { originX: sx, originY: sy, width: sw, height: sh }, descriptor: f.descriptor,
        detection: { box: { x: o.x, y: o.y, width: bb.width / scale, height: bb.height / scale } },
        landmarks: { positions: f.landmarks.positions.map(m) } });
    });
  }
  return out;
}

// Body signature: colour histograms of torso + legs (8 hues x 3 brightness + 8 greys) and build (height / width).
const hsvBin = (r, g, b) => {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn, val = mx / 255;
  if (mx === 0 || d / mx < 0.2) return 24 + Math.min(7, Math.floor(val * 8));
  let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  return Math.floor(h / 45) * 3 + Math.min(2, Math.floor(val * 3));
};
export function bodySig(v, b, canvas) {
  canvas.width = 24; canvas.height = 48;
  const g = canvas.getContext("2d", { willReadFrequently: true });
  g.drawImage(v, b.originX + b.width * 0.15, b.originY, b.width * 0.7, b.height, 0, 0, 24, 48);
  const px = g.getImageData(0, 0, 24, 48).data;
  const hist = (y0, y1) => {
    const h = new Array(32).fill(0);
    for (let y = y0; y < y1; y++) for (let x = 0; x < 24; x++) { const i = (y * 24 + x) * 4; h[hsvBin(px[i], px[i + 1], px[i + 2])]++; }
    const n = (y1 - y0) * 24; return h.map((q) => q / n);
  };
  return { torso: hist(14, 29), legs: hist(29, 46), build: b.height / b.width };
}
const bhat = (a, b) => a.reduce((s, q, i) => s + Math.sqrt(q * b[i]), 0);
export const bodySim = (s, t) => 0.6 * bhat(s.torso, t.torso) + 0.4 * bhat(s.legs, t.legs) - 0.5 * Math.min(0.3, Math.abs(s.build - t.build) / s.build);

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
export const loadDayBodies = () => { try { const d = JSON.parse(localStorage.getItem(DAY_KEY) || "{}"); return d.day === today() ? d.people || {} : {}; } catch (e) { return {}; } };
export const saveDayBody = (people, name, sig) => {
  const old = people[name];
  const mix = (a, b) => a.map((q, i) => 0.8 * q + 0.2 * b[i]);
  people[name] = old ? { torso: mix(old.torso, sig.torso), legs: mix(old.legs, sig.legs), build: 0.8 * old.build + 0.2 * sig.build } : sig;
  try { localStorage.setItem(DAY_KEY, JSON.stringify({ day: today(), people })); } catch (e) { /* ignore */ }
};
